import PropTypes from "prop-types";
import Ad from "../Ad/component";
import Title from "../Title/component";
import FeaturedPolicy from "../ContentArea/library/FeaturedPolicy";
import FeaturedNews from "./components/FeaturedNews/component";
import MobilePolicy from "../ContentArea/library/MobilePolicy";
import { HIGHLIGHTED_STORIES_MAX_COUNT } from "../../constants/content";
import { getNumTopStories } from "../../utils/dataTransform/utils";
import { SECTIONS_WITH_BANNERS } from "../../constants/sections";

/**
 * The FeaturedArea component.
 *
 * This content list displays featured articles
 *
 * @param {Object} props The component props
 * @returns {React.Component} Returns the Icon component
 */
export function FeaturedArea({
    items = null,
    baseSection = "news",
    subSection = "",
    viewport = "viewportLarge",
    path = "",
    hasTopStories = false,
    hasHighlightedStories = true,
    cardClickHandler = () => {},
    contentPackage = null,
    isDenseFeatured,
    displayPolicy,
}) {
    // The default FeaturedPolicy has a specific Mobile view. Others, like Graphical, Text don't apply
    const mobile = viewport === "viewportSmall";

    const mobileBigboxAd =
        mobile === true && baseSection !== "homepage" ? (
            <Ad type="bigbox" targeting={{ plc: "feed" }} />
        ) : null;

    // Indicate that the FeaturedNews component is to be rendered
    if (isDenseFeatured) {
        return (
            <section className="featuredArea sclt-featuredarea">
                <FeaturedNews
                    title={"Featured Content"}
                    mobileAd={mobileBigboxAd}
                    items={items}
                    baseSection="news"
                    subSection={subSection}
                    viewport="viewportLarge"
                    cardClickHandler={cardClickHandler}
                    contentPackage={contentPackage}
                    displayPolicy={displayPolicy}
                />
            </section>
        );
    }

    // Featured display policy needs to show different types of cards depending on viewport,
    // or if the Top Stories section has been requested
    const featuredProps = {
        viewport: viewport,
        hasTopStories: hasTopStories,
        contentPackage: contentPackage,
    };

    let featuredAreaDisplayPolicy =
        displayPolicy !== undefined
            ? displayPolicy
            : new FeaturedPolicy(featuredProps);

    // The default FeaturedPolicy has a specific Mobile view. Others, like Graphical, Text don't apply
    if (featuredAreaDisplayPolicy.richPolicy && mobile) {
        featuredAreaDisplayPolicy = new MobilePolicy({
            contentPackage,
        });
    }

    // Get the formatted cards based on the display policy
    const sections = {
        baseSection,
        subSection,
        trackingSection: "featured",
    };

    const itemList = featuredAreaDisplayPolicy.format(
        items,
        sections,
        cardClickHandler
    );

    // Check if the Featured Area needs to show the "Top Stories" layout.
    // The default number of top stories is defined in the config, but there are sections that override this value
    let numTopStories = hasTopStories ? getNumTopStories(baseSection) : 0;
    if (SECTIONS_WITH_BANNERS[path]) {
        const customTopStories =
            SECTIONS_WITH_BANNERS[path].customNumTopStories;
        numTopStories = customTopStories ? customTopStories : numTopStories;
    }

    // Get the starting index of the Highlighted Stories
    const highlightedStartIndex = getHighlightedStartIndex(
        itemList,
        numTopStories,
        hasHighlightedStories
    );

    // Get the starting index of the Featured Area Stories
    const featuredAreaStartIndex = getFeaturedAreaStartIndex(
        itemList,
        numTopStories,
        highlightedStartIndex,
        hasHighlightedStories
    );

    // Render all parts of the Featured area, depending on the number of items: Top Stories, Highlighted Stories, the rest of Featured
    return (
        <div>
            <section
                className="featuredArea sclt-featuredarea"
                data-cy="topStoriesFeatured"
            >
                <Title level="h2" hidden={true}>
                    Featured Content
                </Title>
                {contentPackage}
                {getTopStories(itemList, hasTopStories, numTopStories)}
                {getHighlightedStories(
                    itemList,
                    highlightedStartIndex,
                    hasHighlightedStories
                )}
                {mobileBigboxAd}
                {getSlice(itemList, featuredAreaStartIndex, itemList.length)}
            </section>
        </div>
    );
}

/**
 * Slice a list based on properties start/end
 * @param {object} itemList The list to be sliced
 * @param {number} startIndex Starting index, from where to start the slicing
 * @param {number} endIndex Ending index, up to where to slice
 * @return {object} The sliced object in an array form
 */
function getSlice(itemList, startIndex, endIndex) {
    if (
        itemList &&
        itemList.length &&
        startIndex !== null &&
        itemList.length > startIndex
    ) {
        // && itemList.size >= endIndex
        let slice = itemList.slice(startIndex, endIndex);
        return slice;
    }
    return null;
}

/**
 * Get the start index of Featured Area Stories after Top Stories and Highlighted Stories
 * @param {object} itemList The story item List
 * @param {number} numTopStories Number of Top Stories
 * @param {number} highlightedStartIndex Highlighted Stories starting index, from where to start the slicing
 * @param {boolean} hasHighlightedStories A flag indicating whether to show highlighted stories
 * @return {number} Starting index of Featured Area Stories, null if no stories to display
 */
function getFeaturedAreaStartIndex(
    itemList,
    numTopStories,
    highlightedStartIndex,
    hasHighlightedStories
) {
    if (highlightedStartIndex === null) {
        // There are no more stories to display after Top Stories
        return null;
    }
    if (!hasHighlightedStories) {
        // There are no Highlighted Stories
        return numTopStories;
    }
    if (
        itemList &&
        itemList.length &&
        itemList.length <= highlightedStartIndex + HIGHLIGHTED_STORIES_MAX_COUNT
    ) {
        // There are no more stories to display after Highlighted Stories
        return null;
    }
    return highlightedStartIndex + HIGHLIGHTED_STORIES_MAX_COUNT;
}

/**
 * Get the start index of Highlighted Stories if there are stories to display after Top Stories
 * @param {object} itemList The story item List
 * @param {number} numTopStories Number of Top Stories
 * @param {boolean} hasHighlightedStories A flag indicating whether to show highlighted stories
 * @return {number} Starting index, null if no stories to display, 0 if highlighed Stories not required
 */
function getHighlightedStartIndex(
    itemList,
    numTopStories,
    hasHighlightedStories
) {
    if (itemList && itemList.length && itemList.length <= numTopStories) {
        // There are no more stories to display after Top Stories
        return null;
    }
    if (hasHighlightedStories) {
        return numTopStories;
    }
    return 0;
}

/**
 * Markup for the Top Stories section
 * @param {object} itemList The list to be sliced
 * @param {number} highlightedStartIndex Highlighted Stories starting index, from where to start the slicing
 * @param {boolean} hasHighlightedStories A flag indicating whether to show highlighted stories
 * @return {object} The JSX for the Highlighted stories
 */
function getHighlightedStories(
    itemList,
    highlightedStartIndex,
    hasHighlightedStories
) {
    if (!itemList || !hasHighlightedStories) {
        return null;
    }

    return (
        <div className="featuredHighlights sclt-featuredHighlights">
            {getSlice(
                itemList,
                highlightedStartIndex,
                highlightedStartIndex + HIGHLIGHTED_STORIES_MAX_COUNT
            )}
        </div>
    );
}

/**
 * Markup for the Top Stories section
 * @param {object} itemList The list to be sliced
 * @param {boolean} hasTopStories A flag indicating whether to show top stories
 * @param {number} numTopStories The number of TOP stories for section (e.g. News=4, Life=3)
 * @return {object} The JSX for the Top stories
 */
function getTopStories(itemList, hasTopStories, numTopStories) {
    if (!itemList || !hasTopStories) {
        return null;
    }

    const topStoriesCards = getSlice(itemList, 0, numTopStories);
    if (!topStoriesCards) {
        return null;
    }

    const firstCard = topStoriesCards.shift();
    const secondaryCards = !topStoriesCards.length ? null : (
        <div className="secondaryTopStories">{topStoriesCards}</div>
    );

    return (
        <div className="featuredTopStories sclt-featuredTopStories">
            <div className="primaryTopStories">{firstCard}</div>
            {secondaryCards}
        </div>
    );
}

FeaturedArea.propTypes = {
    items: PropTypes.array.isRequired,
    baseSection: PropTypes.string,
    subSection: PropTypes.string,
    viewport: PropTypes.string,
    path: PropTypes.string,
    hasTopStories: PropTypes.bool,
    hasHighlightedStories: PropTypes.bool,
    cardClickHandler: PropTypes.func,
    contentPackage: PropTypes.object,
};

export default FeaturedArea;
